import {
  StateField,
  StateEffect,
  Extension,
  Transaction,
  Text,
  RangeSetBuilder,
} from "@codemirror/state";
import type { TransactionSpec } from "@codemirror/state";
import {
  EditorView,
  Decoration,
  DecorationSet,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from "@codemirror/view";

export type DiffState =
  | { status: "idle" }
  | { status: "streaming"; selFrom: number; selTo: number; newText: string }
  | { status: "ready"; selFrom: number; selTo: number; newText: string }
  | { status: "aborted" };

export const startDiff = StateEffect.define<{ selFrom: number; selTo: number }>();
export const appendDiff = StateEffect.define<{ chunk: string }>();
export const finishDiff = StateEffect.define<void>();
export const acceptDiff = StateEffect.define<void>();
export const rejectDiff = StateEffect.define<void>();

function isInlineDiffEffect(e: StateEffect<unknown>): boolean {
  return (
    e.is(startDiff) || e.is(appendDiff) || e.is(finishDiff) || e.is(acceptDiff) || e.is(rejectDiff)
  );
}

export const inlineDiffField = StateField.define<DiffState>({
  create: () => ({ status: "idle" }),
  update(state, tr: Transaction): DiffState {
    // Auto-abort if the document changes via a transaction that is not ours.
    if (tr.docChanged && state.status === "streaming") {
      const hasOurs = tr.effects.some(isInlineDiffEffect);
      if (!hasOurs) return { status: "aborted" };
    }
    for (const e of tr.effects) {
      if (e.is(startDiff)) {
        if (state.status === "streaming" || state.status === "ready") return state;
        return { status: "streaming", selFrom: e.value.selFrom, selTo: e.value.selTo, newText: "" };
      }
      if (e.is(appendDiff)) {
        if (state.status !== "streaming") return state;
        return { ...state, newText: state.newText + e.value.chunk };
      }
      if (e.is(finishDiff)) {
        if (state.status !== "streaming") return state;
        return {
          status: "ready",
          selFrom: state.selFrom,
          selTo: state.selTo,
          newText: state.newText,
        };
      }
      if (e.is(acceptDiff)) {
        return { status: "idle" };
      }
      if (e.is(rejectDiff)) {
        return { status: "idle" };
      }
    }
    return state;
  },
});

export function buildAcceptTransaction(state: DiffState): TransactionSpec | null {
  if (state.status !== "ready") return null;
  return {
    changes: { from: state.selFrom, to: state.selTo, insert: state.newText },
    effects: acceptDiff.of(),
  };
}

class StreamingNewTextWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }
  eq(other: StreamingNewTextWidget) {
    return other.text === this.text;
  }
  toDOM() {
    const wrap = document.createElement("div");
    wrap.className = "cmdk-new";
    const pre = document.createElement("pre");
    pre.textContent = this.text;
    wrap.appendChild(pre);
    return wrap;
  }
  ignoreEvent() {
    return false;
  }
}

function buildDecorations(state: DiffState, doc: Text): DecorationSet {
  if (state.status !== "streaming" && state.status !== "ready") {
    return Decoration.none;
  }
  const builder = new RangeSetBuilder<Decoration>();
  const { selFrom, selTo, newText } = state;
  if (selFrom < selTo) {
    builder.add(selFrom, selTo, Decoration.mark({ attributes: { class: "cmdk-old" } }));
  }
  // Anchor at the end of the line that contains selTo so the widget renders
  // between the selected line and the following line. CM6 forbids block: true
  // in plugin decorations, so we achieve the block-like spacing via CSS.
  const clampedSelTo = Math.min(selTo, doc.length);
  const widgetPos = doc.lineAt(clampedSelTo).to;
  builder.add(
    widgetPos,
    widgetPos,
    Decoration.widget({ widget: new StreamingNewTextWidget(newText), side: 1 })
  );
  return builder.finish();
}

export const inlineDiffViewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view.state.field(inlineDiffField), view.state.doc);
    }
    update(u: ViewUpdate) {
      if (u.state.field(inlineDiffField) !== u.startState.field(inlineDiffField) || u.docChanged) {
        this.decorations = buildDecorations(u.state.field(inlineDiffField), u.state.doc);
      }
    }
  },
  { decorations: (v) => v.decorations }
);

export function inlineDiff(): Extension {
  return [inlineDiffField, inlineDiffViewPlugin];
}
